import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const GameContext = createContext(null);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const { user, updateUserStats } = useAuth();
  const [selectedAIMood, setSelectedAIMood] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [currentStreak, setCurrentStreak] = useState(0);

  // Load game data on mount
  useEffect(() => {
    if (user) {
      loadGameData();
    }
  }, [user]);

  const loadGameData = () => {
    try {
      const gameData = localStorage.getItem(`gameData_${user?.id}`);
      if (gameData) {
        const data = JSON.parse(gameData);
        setGameHistory(data?.history || []);
        setCurrentStreak(data?.currentStreak || 0);
        setSelectedAIMood(data?.selectedAIMood || null);
      }
    } catch (error) {
      console.error('Failed to load game data:', error);
    }
  };

  const saveGameData = (data) => {
    if (!user) return;

    try {
      const currentData = {
        history: gameHistory,
        currentStreak,
        selectedAIMood,
        ...data,
      };
      localStorage.setItem(`gameData_${user?.id}`, JSON.stringify(currentData));
    } catch (error) {
      console.error('Failed to save game data:', error);
    }
  };

  const recordGameResult = (gameResult) => {
    const newHistory = [
      ...gameHistory,
      {
        ...gameResult,
        timestamp: Date.now(),
      },
    ];

    setGameHistory(newHistory);

    // Update streak
    const newStreak = gameResult?.winner === 'player' ? currentStreak + 1 : 0;
    setCurrentStreak(newStreak);

    // Update user stats
    if (gameResult?.winner === 'player') {
      updateUserStats({
        totalWins: (getUserStats()?.totalWins || 0) + 1,
        totalGames: (getUserStats()?.totalGames || 0) + 1,
        currentStreak: newStreak,
        bestStreak: Math.max(getUserStats()?.bestStreak || 0, newStreak),
        totalGems: (getUserStats()?.totalGems || 0) + (gameResult?.gemsEarned || 0),
      });
    } else {
      updateUserStats({
        totalLosses: (getUserStats()?.totalLosses || 0) + 1,
        totalGames: (getUserStats()?.totalGames || 0) + 1,
        currentStreak: 0,
      });
    }

    // Save to localStorage
    saveGameData({
      history: newHistory,
      currentStreak: newStreak,
    });
  };

  const getUserStats = () => {
    if (!user) return null;

    try {
      const usersData = localStorage.getItem('gameUsers');
      const users = usersData ? JSON.parse(usersData) : [];
      const userData = users?.find(u => u?.id === user?.id);
      return userData?.stats || null;
    } catch (error) {
      return null;
    }
  };

  const selectAIMood = (mood) => {
    setSelectedAIMood(mood);
    saveGameData({ selectedAIMood: mood });
  };

  const value = {
    selectedAIMood,
    selectAIMood,
    gameHistory,
    currentStreak,
    recordGameResult,
    getUserStats,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
